import { useImperativeHandle, useRef, useState } from 'react';
import type { Ref } from 'react';
import { ConfigProvider, Slider } from 'antd';
import { PauseOutlined, ReloadOutlined, StepBackwardOutlined, StepForwardOutlined, CaretRightOutlined } from '@ant-design/icons';

import { Button, Select, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { useAmThanhPhien } from '../hooks/useBienBan';
import { dinhDangThoiLuong } from '../nghiep-vu/bien-ban';

export interface DieuKhienPhat {
  /** Tua tới mốc rồi phát; chưa nạp xong thì nhớ mốc, nạp xong mới tua. */
  tua: (giay: number) => void;
  batTat: () => void;
  tuaTuongDoi: (giay: number) => void;
}

const TOC_DO = [0.75, 1, 1.25, 1.5, 2] as const;
const BUOC_TUA = 5;
/** Nút tua mặc định của antd gần như chìm ở nền tối; đi theo màu nhấn để luôn thấy được vị trí đang phát. */
const MAU_THANH_TUA = {
  components: { Slider: { handleColor: 'var(--color-primary-500)', handleActiveColor: 'var(--color-primary-500)' } },
};
/** Số lần tự xin URL ký mới cho một lần hỏng. Hết lượt mà vẫn lỗi thì không phải do hết hạn: báo người dùng. */
const LUOT_XIN_LAI = 2;

/**
 * Trình phát bản ghi âm theo URL ký `GET /audio`.
 *
 * **URL hết hạn:** thẻ `audio` báo lỗi khi tua hoặc nạp tiếp bằng URL đã hết hạn. Lúc đó nhớ vị trí và việc đang phát, xin
 * URL mới, nạp lại rồi phát tiếp đúng chỗ, người nghe chỉ thấy khựng một nhịp. Thử hai lượt vẫn hỏng mới báo lỗi.
 */
export function TrinhPhatAm({
  phienId,
  thoiLuong,
  onThoiGian,
  ref,
}: {
  phienId: string;
  /** Thời lượng của máy chủ, dùng khi tệp không tự báo (WebM ghi từ trình duyệt báo `Infinity`). */
  thoiLuong: number | null;
  onThoiGian: (giay: number) => void;
  ref?: Ref<DieuKhienPhat>;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const q = useAmThanhPhien(phienId);
  const the = useRef<HTMLAudioElement>(null);
  const [dangPhat, setDangPhat] = useState(false);
  const [giay, setGiay] = useState(0);
  const [keo, setKeo] = useState<number | null>(null);
  const [dai, setDai] = useState<number | null>(null);
  const [tocDo, setTocDo] = useState(1);
  const [hong, setHong] = useState(false);
  /** Việc phải làm ngay khi nạp xong: tua tới đâu, có phát tiếp không. */
  const choNap = useRef<{ giay: number; phat: boolean } | null>(null);
  const soLanXin = useRef(0);
  const muonPhat = useRef(false);

  const tongGiay = dai ?? thoiLuong ?? 0;

  const baoThoiGian = (g: number) => {
    setGiay(g);
    onThoiGian(g);
  };

  const phat = () => {
    muonPhat.current = true;
    void the.current?.play().catch(() => {
      // Trình duyệt chặn tự phát hoặc đang nạp lại: nút Phát vẫn còn đó, nhánh lỗi thật do sự kiện `error` lo.
    });
  };

  const tuaDen = (g: number, chayLuon: boolean) => {
    const a = the.current;
    const dich = Math.max(0, tongGiay > 0 ? Math.min(g, tongGiay) : g);
    baoThoiGian(dich);
    if (!a || a.readyState < 1) {
      choNap.current = { giay: dich, phat: chayLuon || muonPhat.current };
      return;
    }
    a.currentTime = dich;
    if (chayLuon) phat();
  };

  const batTat = () => {
    const a = the.current;
    if (!a) return;
    if (a.paused) phat();
    else {
      muonPhat.current = false;
      a.pause();
    }
  };

  useImperativeHandle(ref, () => ({
    tua: (g) => tuaDen(g, true),
    batTat,
    tuaTuongDoi: (d) => tuaDen((the.current?.currentTime ?? giay) + d, false),
  }));

  const xinLaiUrl = () => {
    const a = the.current;
    choNap.current = { giay: a?.currentTime || giay, phat: muonPhat.current };
    const cu = q.data?.url;
    void q.refetch().then((r) => {
      // Máy chủ trả đúng URL cũ thì `src` không đổi, phải tự nạp lại.
      if (r.data?.url === cu) a?.load();
    });
  };

  const khiLoi = () => {
    setDangPhat(false);
    if (soLanXin.current < LUOT_XIN_LAI) {
      soLanXin.current += 1;
      xinLaiUrl();
      return;
    }
    setHong(true);
  };

  const thuLaiTay = () => {
    soLanXin.current = 0;
    setHong(false);
    xinLaiUrl();
  };

  if (q.isLoading) {
    return <Skeleton className="h-16 w-full rounded-lg" />;
  }
  if (q.error) {
    return (
      <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded border border-default bg-subtle px-4 py-3">
        <span className="min-w-0 flex-1 basis-60 text-sm text-content-secondary">{t('bienBan.phat.loiTai', { loi: dichLoi(q.error) })}</span>
        <Button variant="outline" size="sm" onClick={() => void q.refetch()}>{t('chung.thuLai')}</Button>
      </div>
    );
  }

  return (
    <div role="group" aria-label={t('bienBan.phat.nhan')} title={t('bienBan.phat.phimTat')} className="rounded border border-default bg-subtle px-3 py-2.5">
      <audio
        ref={the}
        src={q.data?.url}
        preload="metadata"
        onLoadedMetadata={(e) => {
          const a = e.currentTarget;
          if (Number.isFinite(a.duration) && a.duration > 0) setDai(a.duration);
          a.playbackRate = tocDo;
          const viec = choNap.current;
          choNap.current = null;
          if (viec) {
            if (viec.giay > 0) a.currentTime = viec.giay;
            if (viec.phat) phat();
          }
        }}
        onCanPlay={() => {
          soLanXin.current = 0;
          setHong(false);
        }}
        onTimeUpdate={(e) => baoThoiGian(e.currentTarget.currentTime)}
        onPlay={() => setDangPhat(true)}
        onPause={() => setDangPhat(false)}
        onEnded={() => {
          muonPhat.current = false;
          setDangPhat(false);
        }}
        onError={khiLoi}
      />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" aria-label={t('bienBan.phat.lui')} title={t('bienBan.phat.lui')} icon={<StepBackwardOutlined />} onClick={() => tuaDen((the.current?.currentTime ?? giay) - BUOC_TUA, false)} />
          <Button
            variant="primary"
            size="md"
            aria-label={t(dangPhat ? 'bienBan.phat.tamDung' : 'bienBan.phat.phat')}
            title={t(dangPhat ? 'bienBan.phat.tamDung' : 'bienBan.phat.phat')}
            aria-keyshortcuts="k"
            icon={dangPhat ? <PauseOutlined /> : <CaretRightOutlined />}
            onClick={batTat}
          />
          <Button variant="ghost" size="sm" aria-label={t('bienBan.phat.toi')} title={t('bienBan.phat.toi')} icon={<StepForwardOutlined />} onClick={() => tuaDen((the.current?.currentTime ?? giay) + BUOC_TUA, false)} />
        </div>
        <div className="order-last min-w-0 basis-full sm:order-none sm:flex-1 sm:basis-32">
          <ConfigProvider theme={MAU_THANH_TUA}>
            <Slider
            className="m-0"
            styles={{ track: { background: 'var(--color-primary-500)' }, rail: { background: 'var(--border-strong)' } }}
            min={0}
            max={Math.max(tongGiay, 1)}
            step={1}
            value={keo ?? Math.floor(giay)}
            tooltip={{ formatter: (v) => dinhDangThoiLuong(v ?? 0) }}
            ariaLabelForHandle={t('bienBan.phat.thanhTua')}
            ariaValueTextFormatterForHandle={(v) => dinhDangThoiLuong(v ?? 0) ?? ''}
            onChange={(v) => setKeo(v)}
            onChangeComplete={(v) => {
              setKeo(null);
              tuaDen(v, false);
            }}
          />
          </ConfigProvider>
        </div>
        <span className="whitespace-nowrap font-mono text-xs tabular-nums text-content-secondary">
          {dinhDangThoiLuong(keo ?? giay)} / {dinhDangThoiLuong(tongGiay)}
        </span>
        <Select<number>
          size="small"
          className="min-w-[4.5rem]"
          aria-label={t('bienBan.phat.tocDo')}
          value={tocDo}
          onChange={(v) => {
            setTocDo(v);
            if (the.current) the.current.playbackRate = v;
          }}
          options={TOC_DO.map((v) => ({ value: v, label: `${soVN(v, locale)}×` }))}
        />
      </div>
      {hong && (
        <div role="alert" className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-default pt-2">
          <span className="min-w-0 flex-1 basis-60 text-xs text-danger-700">{t('bienBan.phat.khongPhatDuoc')}</span>
          <Button variant="outline" size="sm" icon={<ReloadOutlined />} onClick={thuLaiTay}>{t('chung.thuLai')}</Button>
        </div>
      )}
    </div>
  );
}
